import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import './EditPredictionModal.css';

const EditPredictionModal = ({ 
  isOpen, 
  onClose, 
  prediction, 
  user, 
  isCreating,
  onSave,
  playoffId
}) => {
  const [homeScore, setHomeScore] = useState('');
  const [awayScore, setAwayScore] = useState('');
  const [skipDuplicateCheck, setSkipDuplicateCheck] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      if (prediction && !isCreating) {
        // Editing existing prediction
        setHomeScore(prediction.home_team_score?.toString() || '');
        setAwayScore(prediction.away_team_score?.toString() || '');
      } else {
        // Creating new prediction
        setHomeScore('');
        setAwayScore('');
      }
      setSkipDuplicateCheck(false);
      setError(null);
    }
  }, [isOpen, prediction, isCreating]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    const home = parseInt(homeScore);
    const away = parseInt(awayScore);

    if (isNaN(home) || isNaN(away) || home < 0 || away < 0) {
      setError('Scores must be non-negative integers');
      setSubmitting(false);
      return;
    }

    try {
      await onSave({
        playoffId,
        predictionId: prediction?.id,
        userId: user?.id || prediction?.user_id,
        homeScore: home,
        awayScore: away,
        skipDuplicateCheck
      });
      onClose();
    } catch (err) {
      console.error('Error saving prediction:', err);
      setError(err.response?.data?.message || 'Failed to save prediction. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="edit-prediction-modal-overlay" onClick={onClose}>
      <div className="edit-prediction-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="edit-prediction-modal-header">
          <h3>
            {isCreating ? `Create Prediction for ${user?.username || 'User'}` : `Edit Prediction for ${prediction?.username || 'User'}`}
          </h3>
          <button 
            className="edit-prediction-modal-close" 
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="edit-prediction-modal-form">
          {error && (
            <div className="edit-prediction-modal-error">
              ❌ {error}
            </div>
          )}

          <div className="edit-prediction-modal-fields">
            <div className="edit-prediction-field">
              <label>
                Home Team Score:
                <input
                  type="number"
                  min="0"
                  value={homeScore}
                  onChange={(e) => setHomeScore(e.target.value)}
                  required
                  disabled={submitting}
                  autoFocus
                />
              </label>
            </div>

            <div className="edit-prediction-field">
              <label>
                Away Team Score:
                <input
                  type="number"
                  min="0"
                  value={awayScore}
                  onChange={(e) => setAwayScore(e.target.value)}
                  required
                  disabled={submitting}
                />
              </label>
            </div>
          </div>

          <div className="edit-prediction-modal-options">
            <label className="edit-prediction-checkbox">
              <input
                type="checkbox"
                checked={skipDuplicateCheck}
                onChange={(e) => setSkipDuplicateCheck(e.target.checked)}
                disabled={submitting}
              />
              <span>Allow duplicate prediction (skip duplicate check)</span>
              <small>⚠️ Only use this if you need to override duplicate validation</small>
            </label>
          </div>

          {prediction && !isCreating && (
            <div className="edit-prediction-modal-info">
              <p><strong>Original Prediction:</strong> {prediction.home_team_score} - {prediction.away_team_score}</p>
              <p><strong>Submitted:</strong> {new Date(prediction.submitted_at).toLocaleString()}</p>
              <p className="edit-prediction-warning">
                ⚠️ Note: If playoff is completed, updating this prediction will reset the playoff status to active and clear calculated results. You will need to recalculate results.
              </p>
            </div>
          )}

          <div className="edit-prediction-modal-actions">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="edit-prediction-modal-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="edit-prediction-modal-save"
            >
              {submitting ? 'Saving...' : isCreating ? 'Create Prediction' : 'Update Prediction'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

EditPredictionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  prediction: PropTypes.shape({
    id: PropTypes.number,
    user_id: PropTypes.number,
    username: PropTypes.string,
    home_team_score: PropTypes.number,
    away_team_score: PropTypes.number,
    submitted_at: PropTypes.string,
  }),
  user: PropTypes.shape({
    id: PropTypes.number,
    username: PropTypes.string,
  }),
  isCreating: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
  playoffId: PropTypes.number.isRequired,
};

export default EditPredictionModal;
